// Popovers da barra de seleção da tela de Contatos (mockup Aeros): adicionar
// tags e atribuir carteira em lote. Cada um dispara um único POST
// (/contacts/bulk-tags, /contacts/bulk-wallet) com os ids selecionados ou
// `all: true` quando o modo "todos da empresa" está ligado.
import React, { useEffect, useState } from "react";
import { makeStyles } from "@material-ui/core/styles";
import Popover from "@material-ui/core/Popover";
import Button from "@material-ui/core/Button";
import Select from "@material-ui/core/Select";
import MenuItem from "@material-ui/core/MenuItem";
import Checkbox from "@material-ui/core/Checkbox";
import CircularProgress from "@material-ui/core/CircularProgress";
import { toast } from "react-toastify";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import { i18n } from "../../translate/i18n";
import useAerosModalStyles from "../../styles/aerosModalStyles";
import { tagChip } from "../../pages/Contacts/contactRules";

const useStyles = makeStyles((theme) => ({
  paper: {
    width: 300,
    padding: 14,
    borderRadius: 12,
    backgroundColor: theme.palette.aeros.surface2 || theme.palette.background.paper,
    border: `1px solid ${theme.palette.type === "light" ? theme.palette.divider : "rgba(255,255,255,0.12)"}`,
    display: "flex",
    flexDirection: "column",
    gap: 10,
  },
  title: { fontSize: 13, fontWeight: 700, color: theme.palette.text.primary },
  hint: { fontSize: 11.5, color: theme.palette.text.secondary },
  list: { maxHeight: 220, overflowY: "auto", display: "flex", flexDirection: "column", ...theme.scrollbarStyles },
  row: {
    display: "flex",
    alignItems: "center",
    gap: 6,
    padding: "2px 0",
    cursor: "pointer",
    fontSize: 12.5,
    color: theme.palette.text.primary,
    "& .MuiCheckbox-root": { padding: 4 },
  },
  chip: { fontSize: 10.5, fontWeight: 600, borderRadius: 5, padding: "1px 7px" },
  actions: { display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 4 },
  empty: { fontSize: 12, color: theme.palette.aeros.textTertiary, padding: "8px 0" },
}));

const anchorProps = {
  anchorOrigin: { vertical: "bottom", horizontal: "right" },
  transformOrigin: { vertical: "top", horizontal: "right" },
};

const payloadDe = (selection) =>
  selection.all ? { all: true } : { contactIds: Array.from(selection.ids || []) };

export const BulkTagPopover = ({ anchorEl, onClose, selection, onDone }) => {
  const classes = useStyles();
  const m = useAerosModalStyles();
  const [tags, setTags] = useState([]);
  const [chosen, setChosen] = useState(new Set());
  const [mode, setMode] = useState("add");
  const [saving, setSaving] = useState(false);
  const open = Boolean(anchorEl);

  useEffect(() => {
    if (!open) return;
    setChosen(new Set());
    setMode("add");
    api
      .get("/tags/list", { params: { kanban: 0 } })
      .then(({ data }) => setTags(Array.isArray(data) ? data : []))
      .catch((err) => toastError(err));
  }, [open]);

  const toggle = (id) =>
    setChosen((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const apply = async () => {
    setSaving(true);
    try {
      const { data } = await api.post("/contacts/bulk-tags", { ...payloadDe(selection), tagIds: Array.from(chosen), mode });
      toast.success(i18n.t(mode === "add" ? "contacts.bulk.tagsAdded" : "contacts.bulk.tagsRemoved", { count: data.contacts }));
      onDone();
      onClose();
    } catch (err) {
      toastError(err);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Popover open={open} anchorEl={anchorEl} onClose={onClose} {...anchorProps} PaperProps={{ className: classes.paper }}>
      <div className={classes.title}>{i18n.t("contacts.bulk.addTag")}</div>
      <Select
        value={mode}
        onChange={(e) => setMode(e.target.value)}
        variant="outlined"
        className={`${m.input} ${m.inputSmall}`}
        MenuProps={{ getContentAnchorEl: null, anchorOrigin: { vertical: "bottom", horizontal: "left" } }}
      >
        <MenuItem value="add" dense>{i18n.t("contacts.bulk.modeAdd")}</MenuItem>
        <MenuItem value="remove" dense>{i18n.t("contacts.bulk.modeRemove")}</MenuItem>
      </Select>
      <div className={classes.list}>
        {tags.length === 0 && <div className={classes.empty}>{i18n.t("contacts.bulk.noTags")}</div>}
        {tags.map((t) => {
          const chip = tagChip(t);
          return (
            <label key={t.id} className={classes.row}>
              <Checkbox color="primary" checked={chosen.has(t.id)} onChange={() => toggle(t.id)} />
              <span className={classes.chip} style={{ color: chip.color, backgroundColor: chip.bg }}>{chip.label}</span>
            </label>
          );
        })}
      </div>
      <div className={classes.actions}>
        <Button variant="outlined" className={m.cancelButton} size="small" onClick={onClose} disabled={saving}>
          {i18n.t("contacts.bulk.cancel")}
        </Button>
        <Button variant="contained" color="primary" className={m.submitButton} size="small" onClick={apply} disabled={saving || chosen.size === 0}>
          {i18n.t("contacts.bulk.apply")}
          {saving && <CircularProgress size={20} className={m.buttonProgress} />}
        </Button>
      </div>
    </Popover>
  );
};

export const BulkWalletPopover = ({ anchorEl, onClose, selection, onDone }) => {
  const classes = useStyles();
  const m = useAerosModalStyles();
  const [users, setUsers] = useState([]);
  const [userId, setUserId] = useState("");
  const [queueId, setQueueId] = useState("");
  const [saving, setSaving] = useState(false);
  const open = Boolean(anchorEl);

  useEffect(() => {
    if (!open) return;
    setUserId("");
    setQueueId("");
    api
      .get("/users/list")
      .then(({ data }) => setUsers(Array.isArray(data) ? data : []))
      .catch((err) => toastError(err));
  }, [open]);

  const user = users.find((u) => u.id === userId);
  const queues = user?.queues || [];

  const apply = async () => {
    setSaving(true);
    try {
      const { data } = await api.post("/contacts/bulk-wallet", {
        ...payloadDe(selection),
        userId: userId === "" ? null : userId,
        queueId: queueId === "" ? null : queueId,
      });
      toast.success(i18n.t(userId === "" ? "contacts.bulk.walletCleared" : "contacts.bulk.walletAssigned", { count: data.contacts }));
      onDone();
      onClose();
    } catch (err) {
      toastError(err);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Popover open={open} anchorEl={anchorEl} onClose={onClose} {...anchorProps} PaperProps={{ className: classes.paper }}>
      <div className={classes.title}>{i18n.t("contacts.bulk.assignWallet")}</div>
      <div>
        <label className={m.fieldLabel}>{i18n.t("contacts.bulk.walletUser")}</label>
        <Select
          value={userId}
          onChange={(e) => {
            setUserId(e.target.value);
            setQueueId("");
          }}
          displayEmpty
          variant="outlined"
          className={`${m.input} ${m.inputSmall}`}
          MenuProps={{ getContentAnchorEl: null, anchorOrigin: { vertical: "bottom", horizontal: "left" } }}
        >
          <MenuItem value="" dense>{i18n.t("contacts.bulk.walletNone")}</MenuItem>
          {users.map((u) => (
            <MenuItem key={u.id} value={u.id} dense>{u.name}</MenuItem>
          ))}
        </Select>
      </div>
      {user && (
        <div>
          <label className={m.fieldLabel}>{i18n.t("contacts.bulk.walletQueue")}</label>
          <Select
            value={queueId}
            onChange={(e) => setQueueId(e.target.value)}
            displayEmpty
            variant="outlined"
            className={`${m.input} ${m.inputSmall}`}
            MenuProps={{ getContentAnchorEl: null, anchorOrigin: { vertical: "bottom", horizontal: "left" } }}
          >
            <MenuItem value="" dense>{i18n.t("contacts.bulk.walletQueueAuto")}</MenuItem>
            {queues.map((q) => (
              <MenuItem key={q.id} value={q.id} dense>{q.name}</MenuItem>
            ))}
          </Select>
        </div>
      )}
      <div className={classes.hint}>{i18n.t("contacts.bulk.walletHint")}</div>
      <div className={classes.actions}>
        <Button variant="outlined" className={m.cancelButton} size="small" onClick={onClose} disabled={saving}>
          {i18n.t("contacts.bulk.cancel")}
        </Button>
        <Button variant="contained" color="primary" className={m.submitButton} size="small" onClick={apply} disabled={saving}>
          {i18n.t("contacts.bulk.apply")}
          {saving && <CircularProgress size={20} className={m.buttonProgress} />}
        </Button>
      </div>
    </Popover>
  );
};
